/**
 * Pure H · Business Reality · Payments + COD operational surface.
 * Single page, two tabs. Reuses existing UI primitives only.
 * All backend endpoints from /app/backend/pureh/payments.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, ArrowLeft, Send, CheckCircle, XCircle, Split, RotateCcw, Link2, GitCompare, Lock } from "lucide-react";

const VENTURE = "orvan";

// ── API client (inline to avoid touching services/api.ts contract) ──
const BASE = (import.meta as any).env?.VITE_API_URL || (window as any).REACT_APP_BACKEND_URL || "";
function apiUrl(p: string) {
  const base = (BASE || (window as any).__PUREH_BASE__ || "") || "";
  return `${base}/api/pureh${p}`;
}
async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(apiUrl(path), {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const j = await r.json(); msg = j.detail || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  if (r.headers.get("content-type")?.includes("application/json")) return r.json();
  return {} as T;
}

// ── Roles ──
type Me = { id?: string; email?: string; role?: string; isHoldingOwner?: boolean };
function useMe(): Me | null {
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => { api<Me>("/auth/me").then(setMe).catch(() => setMe({})); }, []);
  return me;
}
function isPrivileged(me: Me | null) {
  return !!me && ((me.role || "").toLowerCase() === "owner" || (me.role || "").toLowerCase() === "admin" || !!me.isHoldingOwner);
}

// ── Shared types (mirror backend) ──
type PaymentMeta = {
  directions: string[]; methods: string[]; statuses: string[];
  partyTypes: string[]; linkTypes: string[]; transitions: Record<string, string[]>;
};
type Payment = any;
type CODMeta = { lineStatuses: string[]; batchStatuses: string[] };
type CODBatch = any;

const STATUS_TINT: Record<string, { bg: string; fg: string }> = {
  draft: { bg: "#e2e8f0", fg: "#334155" },
  pending: { bg: "#fde68a", fg: "#78350f" },
  confirmed: { bg: "#dbeafe", fg: "#1e40af" },
  partially_allocated: { bg: "#fef3c7", fg: "#78350f" },
  fully_allocated: { bg: "#dcfce7", fg: "#15803d" },
  reversed: { bg: "#fee2e2", fg: "#991b1b" },
  failed: { bg: "#fecaca", fg: "#7f1d1d" },
  cancelled: { bg: "#f1f5f9", fg: "#64748b" },
  open: { bg: "#dbeafe", fg: "#1e40af" },
  in_progress: { bg: "#fef3c7", fg: "#78350f" },
  reconciled: { bg: "#dcfce7", fg: "#15803d" },
  disputed: { bg: "#fee2e2", fg: "#991b1b" },
  closed: { bg: "#e2e8f0", fg: "#334155" },
};

function StatusPill({ status }: { status: string }) {
  const t = STATUS_TINT[status] || { bg: "#f1f5f9", fg: "#334155" };
  return <span data-testid={`status-${status}`}
    className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium"
    style={{ background: t.bg, color: t.fg }}>{status.replace(/_/g, " ")}</span>;
}

// ═══════════════════════════════════════════════════════════════════════════
// PAYMENTS
// ═══════════════════════════════════════════════════════════════════════════
function PaymentsPanel() {
  const me = useMe();
  const priv = isPrivileged(me);
  const [meta, setMeta] = useState<PaymentMeta | null>(null);
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Payment | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState({ direction: "", method: "", status: "" });

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(Object.entries(filter).filter(([, v]) => v)).toString();
      const list = await api<Payment[]>(`/finance/ventures/${VENTURE}/payments${qs ? `?${qs}` : ""}`);
      setRows(list);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { api<PaymentMeta>("/finance/payments/meta").then(setMeta); }, []);
  useEffect(() => { reload(); }, [reload]);

  const refreshOne = async (id: string) => {
    const fresh = await api<Payment>(`/finance/ventures/${VENTURE}/payments/${id}`);
    setSelected(fresh);
    setRows(rs => rs.map(r => r.id === id ? fresh : r));
  };

  return (
    <div data-testid="payments-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--ph-ink-strong)" }}>Payments</h2>
          <div className="text-xs text-slate-500 mt-1">Incoming and outgoing. Confirm / allocate / reverse actions are owner-gated.</div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="filter-direction" className="px-2 py-1.5 text-xs border rounded" value={filter.direction} onChange={e => setFilter(f => ({ ...f, direction: e.target.value }))}>
            <option value="">Any direction</option>
            {(meta?.directions || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid="filter-method" className="px-2 py-1.5 text-xs border rounded" value={filter.method} onChange={e => setFilter(f => ({ ...f, method: e.target.value }))}>
            <option value="">Any method</option>
            {(meta?.methods || []).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="filter-status" className="px-2 py-1.5 text-xs border rounded" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
            <option value="">Any status</option>
            {(meta?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="payments-refresh" onClick={reload} className="p-1.5 border rounded"><RefreshCw size={12} /></button>
          <button data-testid="payment-new-btn" onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white"
            style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)" }}>
            <Plus size={12} /> New payment
          </button>
        </div>
      </div>

      {error && <div className="text-xs mb-3 p-2 rounded" style={{ background: "#fef2f2", color: "#b91c1c" }} data-testid="payments-error">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-3">
          <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                <tr>
                  <th className="text-left px-3 py-2">Doc #</th>
                  <th className="text-left px-3 py-2">Dir</th>
                  <th className="text-left px-3 py-2">Method</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Party</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="text-xs text-center text-slate-400 py-6">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={6} className="text-xs text-center text-slate-400 py-6">No payments.</td></tr>}
                {rows.map(p => (
                  <tr key={p.id} onClick={() => setSelected(p)} data-testid={`payment-row-${p.id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    style={{ borderTop: "1px solid var(--ph-border-subtle)", background: selected?.id === p.id ? "#f8fafc" : "" }}>
                    <td className="px-3 py-2 font-mono text-xs">{p.docNumber}</td>
                    <td className="px-3 py-2 text-xs">{p.direction === "incoming" ? "↓ in" : "↑ out"}</td>
                    <td className="px-3 py-2 text-xs">{p.method}</td>
                    <td className="px-3 py-2 text-right font-medium">{p.currency} {Number(p.amount).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{p.party?.name || p.party?.type}</td>
                    <td className="px-3 py-2 text-xs"><StatusPill status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selected ? <PaymentDetail payment={selected} meta={meta} priv={priv} refresh={refreshOne} onClose={() => setSelected(null)} /> : <div className="text-xs text-slate-400 p-6 text-center" data-testid="payment-detail-empty">Select a payment to inspect.</div>}
        </div>
      </div>

      {showCreate && <PaymentCreateDialog meta={meta} onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await reload(); }} />}
    </div>
  );
}

function PaymentDetail({ payment, meta, priv, refresh, onClose }: {
  payment: Payment; meta: PaymentMeta | null; priv: boolean;
  refresh: (id: string) => Promise<void>; onClose: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showAlloc, setShowAlloc] = useState(false);
  const [showReverse, setShowReverse] = useState(false);
  const status = payment.status;
  const allowedTransitions = meta?.transitions?.[status] || [];

  const doAction = async (verb: string, url: string, body?: any) => {
    setBusy(verb); setErr(null);
    try {
      await api(url, { method: "POST", body: body ? JSON.stringify(body) : undefined });
      await refresh(payment.id);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-4" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }} data-testid="payment-detail">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[11px] text-slate-400">{payment.docNumber}</div>
          <div className="text-sm font-medium"><StatusPill status={status} /></div>
        </div>
        <button onClick={onClose} className="text-xs text-slate-400" data-testid="payment-close-detail"><ArrowLeft size={12} /></button>
      </div>

      <dl className="space-y-1 text-xs mb-3">
        <div><dt className="inline text-slate-500">Direction:</dt> <dd className="inline">{payment.direction}</dd></div>
        <div><dt className="inline text-slate-500">Method:</dt> <dd className="inline">{payment.method}</dd></div>
        <div><dt className="inline text-slate-500">Amount:</dt> <dd className="inline font-medium">{payment.currency} {Number(payment.amount).toFixed(2)}</dd> · fees {payment.fees || 0} · net {Number(payment.netAmount).toFixed(2)}</div>
        <div><dt className="inline text-slate-500">Party:</dt> <dd className="inline">{payment.party?.type} · {payment.party?.name || "—"}</dd></div>
        <div><dt className="inline text-slate-500">Ref:</dt> <dd className="inline">{payment.referenceNumber || "—"}</dd></div>
        <div><dt className="inline text-slate-500">Linked:</dt> <dd className="inline">{payment.linkedObject ? `${payment.linkedObject.type} · ${payment.linkedObject.id}` : "—"}</dd></div>
        {payment.reversalOf && <div className="text-red-600">Reversal of {payment.reversalOf}</div>}
      </dl>

      {err && <div className="text-xs mb-2 p-2 rounded" style={{ background: "#fef2f2", color: "#b91c1c" }} data-testid="payment-detail-error">{err}</div>}

      {/* Actions row */}
      <div className="flex flex-wrap gap-1 mb-3">
        {allowedTransitions.includes("pending") && (
          <ActionBtn tid="payment-submit" icon={Send} label="Submit" busy={busy === "submit"}
            onClick={() => doAction("submit", `/finance/ventures/${VENTURE}/payments/${payment.id}/submit`)} />
        )}
        {(allowedTransitions.includes("confirmed")) && (
          <ActionBtn tid="payment-confirm" icon={CheckCircle} label="Confirm"
            disabled={!priv} busy={busy === "confirm"}
            onClick={() => doAction("confirm", `/finance/ventures/${VENTURE}/payments/${payment.id}/confirm`)} />
        )}
        {(["confirmed", "partially_allocated"].includes(status)) && (
          <ActionBtn tid="payment-allocate" icon={Split} label="Allocate"
            disabled={!priv} onClick={() => setShowAlloc(true)} />
        )}
        {(["confirmed", "partially_allocated", "fully_allocated"].includes(status)) && (
          <ActionBtn tid="payment-reverse" icon={RotateCcw} label="Reverse"
            disabled={!priv} onClick={() => setShowReverse(true)} />
        )}
        {allowedTransitions.includes("cancelled") && (
          <ActionBtn tid="payment-cancel" icon={XCircle} label="Cancel" busy={busy === "cancel"}
            onClick={() => doAction("cancel", `/finance/ventures/${VENTURE}/payments/${payment.id}/cancel`)} />
        )}
        {allowedTransitions.includes("failed") && (
          <ActionBtn tid="payment-fail" icon={XCircle} label="Fail" busy={busy === "fail"}
            onClick={() => doAction("fail", `/finance/ventures/${VENTURE}/payments/${payment.id}/fail`)} />
        )}
      </div>

      {/* Allocations */}
      {(payment.allocations || []).length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">Allocations</div>
          <ul className="text-xs space-y-0.5" data-testid="payment-allocations">
            {payment.allocations.map((a: any) => (
              <li key={a.id} className="flex justify-between"><span>{a.targetType} · <code>{a.targetId}</code></span><span>{Number(a.amount).toFixed(2)}</span></li>
            ))}
          </ul>
        </div>
      )}

      {/* Status history */}
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-500" data-testid="payment-history-toggle">Status history ({(payment.statusHistory || []).length})</summary>
        <ul className="mt-1 space-y-1 border-t pt-1">
          {(payment.statusHistory || []).map((h: any, i: number) => (
            <li key={i} data-testid={`payment-history-${i}`}>
              <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span>{" · "}
              <StatusPill status={h.to} />{" "}by {h.by}{h.reason && <span className="text-slate-400"> · {h.reason}</span>}
            </li>
          ))}
        </ul>
      </details>

      {/* Attachments */}
      {(payment.attachments || []).length > 0 && (
        <details className="text-xs mt-2">
          <summary className="cursor-pointer text-slate-500">Attachments ({payment.attachments.length})</summary>
          <ul className="mt-1">
            {payment.attachments.map((a: string) => (
              <li key={a}><code>{a}</code></li>
            ))}
          </ul>
        </details>
      )}

      {showAlloc && (
        <AllocateDialog payment={payment} onClose={() => setShowAlloc(false)}
          onDone={async () => { setShowAlloc(false); await refresh(payment.id); }} />
      )}
      {showReverse && (
        <ReverseDialog payment={payment} onClose={() => setShowReverse(false)}
          onDone={async () => { setShowReverse(false); await refresh(payment.id); }} />
      )}
    </div>
  );
}

function ActionBtn({ tid, icon: Icon, label, onClick, disabled, busy }: { tid: string; icon: any; label: string; onClick: () => void; disabled?: boolean; busy?: boolean }) {
  return (
    <button data-testid={tid} onClick={onClick} disabled={disabled || busy}
      title={disabled ? "Owner/admin only" : ""}
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border"
      style={{
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        background: "var(--ph-surface)",
      }}>
      {disabled ? <Lock size={10} /> : <Icon size={11} />}{" "}{label}{busy && "…"}
    </button>
  );
}

function PaymentCreateDialog({ meta, onClose, onCreated }: { meta: PaymentMeta | null; onClose: () => void; onCreated: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    direction: "incoming", method: "bank_transfer", amount: "",
    currency: "EGP", partyType: "customer", partyName: "",
    referenceNumber: "", bankAccount: "", notes: "", fees: "",
    linkedType: "", linkedId: "",
  });
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/finance/ventures/${VENTURE}/payments`, {
        method: "POST", body: JSON.stringify({
          direction: form.direction, method: form.method,
          amount: Number(form.amount), currency: form.currency,
          fees: Number(form.fees || 0),
          party: { type: form.partyType, name: form.partyName || null },
          referenceNumber: form.referenceNumber || undefined,
          bankAccount: form.bankAccount || undefined,
          notes: form.notes || undefined,
          linkedObject: form.linkedType && form.linkedId ? { type: form.linkedType, id: form.linkedId } : undefined,
        }),
      });
      await onCreated();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md p-6 mx-4" style={{ background: "white", borderRadius: 8 }} onClick={e => e.stopPropagation()} data-testid="payment-create-dialog">
        <h3 className="text-base font-medium mb-4">New payment</h3>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>Direction<select data-testid="pc-direction" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}>{(meta?.directions || []).map(d => <option key={d}>{d}</option>)}</select></label>
          <label>Method<select data-testid="pc-method" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>{(meta?.methods || []).map(m => <option key={m}>{m}</option>)}</select></label>
          <label>Amount<input data-testid="pc-amount" type="number" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></label>
          <label>Currency<select data-testid="pc-currency" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>{["EGP","USD","EUR","SAR","CNY"].map(c => <option key={c}>{c}</option>)}</select></label>
          <label>Fees<input data-testid="pc-fees" type="number" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.fees} onChange={e => setForm(f => ({ ...f, fees: e.target.value }))} /></label>
          <label>Party type<select data-testid="pc-party-type" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.partyType} onChange={e => setForm(f => ({ ...f, partyType: e.target.value }))}>{(meta?.partyTypes || []).map(p => <option key={p}>{p}</option>)}</select></label>
          <label className="col-span-2">Party name<input data-testid="pc-party-name" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.partyName} onChange={e => setForm(f => ({ ...f, partyName: e.target.value }))} /></label>
          <label className="col-span-2">Reference #<input data-testid="pc-ref" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.referenceNumber} onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))} /></label>
          <label>Linked type<select data-testid="pc-link-type" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.linkedType} onChange={e => setForm(f => ({ ...f, linkedType: e.target.value }))}><option value="">—</option>{(meta?.linkTypes || []).map(l => <option key={l}>{l}</option>)}</select></label>
          <label>Linked ID<input data-testid="pc-link-id" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.linkedId} onChange={e => setForm(f => ({ ...f, linkedId: e.target.value }))} /></label>
          <label className="col-span-2">Notes<textarea data-testid="pc-notes" rows={2} className="w-full mt-1 px-2 py-1.5 border rounded" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></label>
        </div>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="pc-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-5">
          <button data-testid="pc-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="pc-save" disabled={busy || !form.amount} onClick={submit}
            className="px-3 py-1.5 text-xs text-white" style={{ background: "var(--ph-brand-primary)", borderRadius: 6, opacity: busy ? 0.6 : 1 }}>
            {busy ? "Saving…" : "Create draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AllocateDialog({ payment, onClose, onDone }: { payment: Payment; onClose: () => void; onDone: () => Promise<void> }) {
  const [rows, setRows] = useState([{ targetType: "invoice", targetId: "", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/finance/ventures/${VENTURE}/payments/${payment.id}/allocate`, {
        method: "POST", body: JSON.stringify({
          allocations: rows.filter(r => r.targetId && Number(r.amount) > 0).map(r => ({
            targetType: r.targetType, targetId: r.targetId, amount: Number(r.amount),
          })),
        }),
      });
      await onDone();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const remaining = Number(payment.amount) - (payment.allocations || []).reduce((s: number, a: any) => s + Number(a.amount || 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" onClick={onClose}>
      <div className="w-full max-w-md p-6 mx-4" style={{ background: "white", borderRadius: 8 }} onClick={e => e.stopPropagation()} data-testid="allocate-dialog">
        <h3 className="text-base font-medium mb-2">Allocate {payment.currency} {payment.amount}</h3>
        <div className="text-xs text-slate-500 mb-3">Remaining: {remaining.toFixed(2)} · This batch: {total.toFixed(2)}</div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-6 gap-1 text-xs" data-testid={`alloc-row-${i}`}>
              <select className="col-span-2 px-2 py-1 border rounded" value={r.targetType} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, targetType: e.target.value } : x))}>
                {["invoice","expense","investment","cod_batch","other"].map(t => <option key={t}>{t}</option>)}
              </select>
              <input placeholder="target id" data-testid={`alloc-target-${i}`} className="col-span-3 px-2 py-1 border rounded" value={r.targetId} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, targetId: e.target.value } : x))} />
              <input type="number" placeholder="amt" data-testid={`alloc-amount-${i}`} className="col-span-1 px-2 py-1 border rounded" value={r.amount} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
            </div>
          ))}
        </div>
        <button className="mt-2 text-xs text-slate-500" onClick={() => setRows(rs => [...rs, { targetType: "invoice", targetId: "", amount: "" }])} data-testid="alloc-add-row">+ line</button>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="allocate-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="allocate-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="allocate-submit" onClick={submit} disabled={busy} className="px-3 py-1.5 text-xs text-white" style={{ background: "var(--ph-brand-primary)", borderRadius: 6, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Allocate"}</button>
        </div>
      </div>
    </div>
  );
}

function ReverseDialog({ payment, onClose, onDone }: { payment: Payment; onClose: () => void; onDone: () => Promise<void> }) {
  const [reason, setReason] = useState(""); const [ref, setRef] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/finance/ventures/${VENTURE}/payments/${payment.id}/reverse`, {
        method: "POST", body: JSON.stringify({ reason, referenceNumber: ref || undefined }),
      });
      await onDone();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" onClick={onClose}>
      <div className="w-full max-w-md p-6 mx-4" style={{ background: "white", borderRadius: 8 }} onClick={e => e.stopPropagation()} data-testid="reverse-dialog">
        <h3 className="text-base font-medium mb-3">Reverse {payment.docNumber}</h3>
        <label className="block text-xs">Reason<input data-testid="rev-reason" className="w-full mt-1 px-2 py-1.5 border rounded" value={reason} onChange={e => setReason(e.target.value)} /></label>
        <label className="block text-xs mt-2">Reference #<input data-testid="rev-ref" className="w-full mt-1 px-2 py-1.5 border rounded" value={ref} onChange={e => setRef(e.target.value)} /></label>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="reverse-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="rev-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="rev-submit" onClick={submit} disabled={busy || !reason.trim()} className="px-3 py-1.5 text-xs text-white bg-red-600 rounded">{busy ? "…" : "Reverse"}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COD BATCHES
// ═══════════════════════════════════════════════════════════════════════════
function CODPanel() {
  const me = useMe();
  const priv = isPrivileged(me);
  const [meta, setMeta] = useState<CODMeta | null>(null);
  const [rows, setRows] = useState<CODBatch[]>([]);
  const [selected, setSelected] = useState<CODBatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setRows(await api<CODBatch[]>(`/finance/ventures/${VENTURE}/cod-batches`)); }
    catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { api<CODMeta>("/finance/cod-batches/meta").then(setMeta); }, []);
  useEffect(() => { reload(); }, [reload]);
  const refreshOne = async (id: string) => {
    const fresh = await api<CODBatch>(`/finance/ventures/${VENTURE}/cod-batches/${id}`);
    setSelected(fresh);
    setRows(rs => rs.map(r => r.id === id ? fresh : r));
  };
  return (
    <div data-testid="cod-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--ph-ink-strong)" }}>COD reconciliation</h2>
          <div className="text-xs text-slate-500 mt-1">Courier settlement batches. Close-gate: all lines resolved · payment linked · allocations match · variance zero-or-approved.</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="cod-refresh" onClick={reload} className="p-1.5 border rounded"><RefreshCw size={12} /></button>
          <button data-testid="cod-new-btn" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white" style={{ background: "var(--ph-brand-primary)", borderRadius: 6 }}>
            <Plus size={12} /> New batch
          </button>
        </div>
      </div>
      {err && <div className="text-xs text-red-600 mb-3" data-testid="cod-error">{err}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <table className="w-full text-sm" data-testid="cod-table">
              <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                <tr><th className="text-left px-3 py-2">Doc</th><th className="text-left px-3 py-2">Courier</th><th className="text-right px-3 py-2">Net</th><th className="text-left px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="text-xs text-center text-slate-400 py-6">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={4} className="text-xs text-center text-slate-400 py-6">No batches.</td></tr>}
                {rows.map(b => (
                  <tr key={b.id} onClick={() => setSelected(b)} data-testid={`cod-row-${b.id}`} className="cursor-pointer hover:bg-slate-50"
                    style={{ borderTop: "1px solid var(--ph-border-subtle)", background: selected?.id === b.id ? "#f8fafc" : "" }}>
                    <td className="px-3 py-2 font-mono text-xs">{b.docNumber}</td>
                    <td className="px-3 py-2 text-xs">{b.courier}</td>
                    <td className="px-3 py-2 text-right text-xs">{(b.totals?.netRemittance || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs"><StatusPill status={b.reconciliationStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lg:col-span-3">
          {selected ? <CODDetail batch={selected} priv={priv} meta={meta} refresh={refreshOne} onClose={() => setSelected(null)} /> : <div className="text-xs text-slate-400 p-6 text-center" data-testid="cod-detail-empty">Select a batch.</div>}
        </div>
      </div>
      {showCreate && <CODCreateDialog meta={meta} onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await reload(); }} />}
    </div>
  );
}

function CODCreateDialog({ meta, onClose, onCreated }: { meta: CODMeta | null; onClose: () => void; onCreated: () => Promise<void> }) {
  const [form, setForm] = useState({ courier: "", statementNumber: "", statementDate: "", from: "", to: "", openingAmount: "" });
  const [lines, setLines] = useState<any[]>([{ orderRef: "", shipmentRef: "", shipmentStatus: "delivered", expectedCod: "", collectedCod: "", courierFee: "", returnFee: "", otherDeductions: "", invoiceRef: "" }]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/finance/ventures/${VENTURE}/cod-batches`, {
        method: "POST", body: JSON.stringify({
          courier: form.courier, statementNumber: form.statementNumber || undefined,
          statementDate: form.statementDate || undefined,
          settlementPeriodFrom: form.from || undefined,
          settlementPeriodTo: form.to || undefined,
          openingAmount: Number(form.openingAmount || 0),
          lines: lines.filter(l => l.orderRef.trim()).map(l => ({
            orderRef: l.orderRef, shipmentRef: l.shipmentRef || undefined,
            shipmentStatus: l.shipmentStatus,
            expectedCod: Number(l.expectedCod || 0), collectedCod: Number(l.collectedCod || 0),
            courierFee: Number(l.courierFee || 0), returnFee: Number(l.returnFee || 0),
            otherDeductions: Number(l.otherDeductions || 0),
            invoiceRef: l.invoiceRef || undefined,
          })),
        }),
      });
      await onCreated();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl p-6" style={{ background: "white", borderRadius: 8 }} onClick={e => e.stopPropagation()} data-testid="cod-create-dialog">
        <h3 className="text-base font-medium mb-3">New COD settlement batch</h3>
        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <label>Courier<input data-testid="cc-courier" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.courier} onChange={e => setForm(f => ({ ...f, courier: e.target.value }))} /></label>
          <label>Statement #<input data-testid="cc-stmt-no" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.statementNumber} onChange={e => setForm(f => ({ ...f, statementNumber: e.target.value }))} /></label>
          <label>Statement date<input data-testid="cc-stmt-date" type="date" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.statementDate} onChange={e => setForm(f => ({ ...f, statementDate: e.target.value }))} /></label>
          <label>Period from<input data-testid="cc-from" type="date" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))} /></label>
          <label>Period to<input data-testid="cc-to" type="date" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} /></label>
          <label>Opening<input data-testid="cc-opening" type="number" className="w-full mt-1 px-2 py-1.5 border rounded" value={form.openingAmount} onChange={e => setForm(f => ({ ...f, openingAmount: e.target.value }))} /></label>
        </div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">Lines</div>
        <div className="space-y-1">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 text-[11px]" data-testid={`cc-line-${i}`}>
              <input placeholder="order ref" data-testid={`cc-line-${i}-ref`} className="col-span-2 px-2 py-1 border rounded" value={l.orderRef} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, orderRef: e.target.value } : x))} />
              <input placeholder="AWB" className="col-span-2 px-2 py-1 border rounded" value={l.shipmentRef} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, shipmentRef: e.target.value } : x))} />
              <select data-testid={`cc-line-${i}-status`} className="col-span-2 px-2 py-1 border rounded" value={l.shipmentStatus} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, shipmentStatus: e.target.value } : x))}>
                {(meta?.lineStatuses || []).map(s => <option key={s}>{s}</option>)}
              </select>
              <input type="number" placeholder="exp" data-testid={`cc-line-${i}-exp`} className="col-span-1 px-2 py-1 border rounded" value={l.expectedCod} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, expectedCod: e.target.value } : x))} />
              <input type="number" placeholder="col" data-testid={`cc-line-${i}-col`} className="col-span-1 px-2 py-1 border rounded" value={l.collectedCod} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, collectedCod: e.target.value } : x))} />
              <input type="number" placeholder="fee" className="col-span-1 px-2 py-1 border rounded" value={l.courierFee} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, courierFee: e.target.value } : x))} />
              <input placeholder="inv ref" data-testid={`cc-line-${i}-inv`} className="col-span-2 px-2 py-1 border rounded" value={l.invoiceRef} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, invoiceRef: e.target.value } : x))} />
              <input type="number" placeholder="ret" className="col-span-1 px-2 py-1 border rounded" value={l.returnFee} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, returnFee: e.target.value } : x))} />
            </div>
          ))}
        </div>
        <button onClick={() => setLines(ls => [...ls, { orderRef: "", shipmentRef: "", shipmentStatus: "delivered", expectedCod: "", collectedCod: "", courierFee: "", returnFee: "", otherDeductions: "", invoiceRef: "" }])} className="text-xs text-slate-500 mt-2" data-testid="cc-add-line">+ line</button>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="cc-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="cc-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="cc-save" disabled={busy || !form.courier.trim()} onClick={submit} className="px-3 py-1.5 text-xs text-white" style={{ background: "var(--ph-brand-primary)", borderRadius: 6, opacity: busy ? 0.6 : 1 }}>{busy ? "…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function CODDetail({ batch, priv, meta, refresh, onClose }: { batch: CODBatch; priv: boolean; meta: CODMeta | null; refresh: (id: string) => Promise<void>; onClose: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showLink, setShowLink] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const doAction = async (verb: string, url: string, body?: any) => {
    setBusy(verb); setErr(null);
    try { await api(url, { method: "POST", body: body ? JSON.stringify(body) : undefined }); await refresh(batch.id); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  };
  const patchLine = async (lineId: string, patch: any) => {
    try { await api(`/finance/ventures/${VENTURE}/cod-batches/${batch.id}/lines/${lineId}`, { method: "PATCH", body: JSON.stringify(patch) }); await refresh(batch.id); }
    catch (e) { setErr((e as Error).message); }
  };
  return (
    <div className="p-4" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }} data-testid="cod-detail">
      <div className="flex items-center justify-between mb-3">
        <div><div className="font-mono text-[11px] text-slate-400">{batch.docNumber}</div><StatusPill status={batch.reconciliationStatus} /></div>
        <button onClick={onClose} className="text-xs text-slate-400" data-testid="cod-close-detail"><ArrowLeft size={12} /></button>
      </div>
      <dl className="text-xs space-y-1 mb-3">
        <div><dt className="inline text-slate-500">Courier:</dt> <dd className="inline">{batch.courier} · {batch.statementNumber || "no stmt"}</dd></div>
        <div><dt className="inline text-slate-500">Period:</dt> <dd className="inline">{batch.settlementPeriod?.from || "—"} → {batch.settlementPeriod?.to || "—"}</dd></div>
        <div><dt className="inline text-slate-500">Totals:</dt> <dd className="inline">exp {batch.totals?.expectedCod?.toFixed(2)} · coll {batch.totals?.collectedCod?.toFixed(2)} · fee {batch.totals?.courierFee?.toFixed(2)} · net <b>{batch.totals?.netRemittance?.toFixed(2)}</b></dd></div>
        <div><dt className="inline text-slate-500">Bank received:</dt> <dd className="inline font-medium">{Number(batch.bankReceivedAmount || 0).toFixed(2)}</dd></div>
        <div><dt className="inline text-slate-500">Variance:</dt> <dd className="inline" style={{ color: Math.abs(batch.variance || 0) < 0.005 ? "#15803d" : "#b91c1c" }}>{Number(batch.variance || 0).toFixed(2)}</dd> {batch.varianceApproval && <span className="text-xs text-slate-500"> · approved by {batch.varianceApproval.by}</span>}</div>
        <div><dt className="inline text-slate-500">Linked payment:</dt> <dd className="inline">{batch.linkedPaymentId ? <code>{batch.linkedPaymentId}</code> : "—"}</dd></div>
      </dl>

      <div className="flex flex-wrap gap-1 mb-3">
        {["open","in_progress","disputed","reconciled"].includes(batch.reconciliationStatus) && (
          <ActionBtn tid="cod-link-payment" icon={Link2} label="Link payment" disabled={!priv} onClick={() => setShowLink(true)} />
        )}
        {["open","in_progress","disputed","reconciled"].includes(batch.reconciliationStatus) && (
          <ActionBtn tid="cod-reconcile" icon={GitCompare} label="Reconcile" disabled={!priv} busy={busy === "reconcile"}
            onClick={() => doAction("reconcile", `/finance/ventures/${VENTURE}/cod-batches/${batch.id}/reconcile`)} />
        )}
        {Math.abs(batch.variance || 0) > 0.005 && !batch.varianceApproval && (
          <ActionBtn tid="cod-approve-variance" icon={CheckCircle} label="Approve variance" disabled={!priv} onClick={() => setShowApprove(true)} />
        )}
        {batch.reconciliationStatus !== "closed" && (
          <ActionBtn tid="cod-close" icon={Lock} label="Close" disabled={!priv} busy={busy === "close"}
            onClick={() => doAction("close", `/finance/ventures/${VENTURE}/cod-batches/${batch.id}/close`)} />
        )}
      </div>

      {err && <div className="text-xs mb-3 p-2 rounded" style={{ background: "#fef2f2", color: "#b91c1c" }} data-testid="cod-detail-error">{err}</div>}

      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">Lines ({(batch.lines || []).length})</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="cod-lines-table">
          <thead className="text-slate-500"><tr>
            <th className="text-left">Order</th><th className="text-left">Status</th><th className="text-right">Exp</th><th className="text-right">Coll</th><th className="text-left">Inv</th><th>Match</th><th>Disp</th>
          </tr></thead>
          <tbody>
            {(batch.lines || []).map((l: any) => (
              <tr key={l.id} data-testid={`cod-line-${l.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                <td>{l.orderRef}</td><td>{l.shipmentStatus}</td>
                <td className="text-right">{Number(l.expectedCod || 0).toFixed(2)}</td>
                <td className="text-right">{Number(l.collectedCod || 0).toFixed(2)}</td>
                <td>{l.invoiceRef || "—"}</td>
                <td className="text-center">{l.matched ? "✓" : "—"}</td>
                <td className="text-center">
                  {l.disputed ? (
                    <span title={l.disputeReason || ""}>
                      <input placeholder="resolution" data-testid={`cod-line-${l.id}-resolution`} className="w-20 px-1 border rounded text-[10px]" defaultValue={l.resolution || ""} onBlur={e => e.target.value && e.target.value !== l.resolution && patchLine(l.id, { resolution: e.target.value })} />
                    </span>
                  ) : (
                    <button data-testid={`cod-line-${l.id}-dispute`} className="text-[10px] text-red-600" onClick={() => { const reason = prompt("Dispute reason?"); if (reason) patchLine(l.id, { disputed: true, disputeReason: reason }); }}>flag</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="text-xs mt-3">
        <summary className="cursor-pointer text-slate-500" data-testid="cod-audit-toggle">Audit trail ({(batch.auditLog || []).length})</summary>
        <ul className="mt-1">
          {(batch.auditLog || []).map((a: any, i: number) => (
            <li key={i} data-testid={`cod-audit-${i}`}><span className="text-slate-400">{new Date(a.at).toLocaleString()}</span> · {a.action} by {a.by}</li>
          ))}
        </ul>
      </details>

      {showLink && <LinkPaymentDialog batch={batch} onClose={() => setShowLink(false)} onDone={async () => { setShowLink(false); await refresh(batch.id); }} />}
      {showApprove && <ApproveVarianceDialog batch={batch} onClose={() => setShowApprove(false)} onDone={async () => { setShowApprove(false); await refresh(batch.id); }} />}
    </div>
  );
}

function LinkPaymentDialog({ batch, onClose, onDone }: { batch: CODBatch; onClose: () => void; onDone: () => Promise<void> }) {
  const [paymentId, setPaymentId] = useState(""); const [amount, setAmount] = useState(String(batch.totals?.netRemittance || 0));
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/finance/ventures/${VENTURE}/cod-batches/${batch.id}/link-payment`, {
        method: "POST", body: JSON.stringify({ paymentId, bankReceivedAmount: Number(amount) }),
      });
      await onDone();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" onClick={onClose}>
      <div className="w-full max-w-md p-6" style={{ background: "white", borderRadius: 8 }} onClick={e => e.stopPropagation()} data-testid="link-payment-dialog">
        <h3 className="text-base font-medium mb-3">Link incoming COD payment</h3>
        <label className="block text-xs">Payment ID<input data-testid="lp-payment-id" className="w-full mt-1 px-2 py-1.5 border rounded" value={paymentId} onChange={e => setPaymentId(e.target.value)} placeholder="pay-..." /></label>
        <label className="block text-xs mt-2">Bank received amount<input data-testid="lp-amount" type="number" className="w-full mt-1 px-2 py-1.5 border rounded" value={amount} onChange={e => setAmount(e.target.value)} /></label>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="lp-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="lp-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="lp-submit" onClick={submit} disabled={busy || !paymentId} className="px-3 py-1.5 text-xs text-white" style={{ background: "var(--ph-brand-primary)", borderRadius: 6 }}>{busy ? "…" : "Link"}</button>
        </div>
      </div>
    </div>
  );
}

function ApproveVarianceDialog({ batch, onClose, onDone }: { batch: CODBatch; onClose: () => void; onDone: () => Promise<void> }) {
  const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await api(`/finance/ventures/${VENTURE}/cod-batches/${batch.id}/approve-variance`, { method: "POST", body: JSON.stringify({ reason }) }); await onDone(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" onClick={onClose}>
      <div className="w-full max-w-md p-6" style={{ background: "white", borderRadius: 8 }} onClick={e => e.stopPropagation()} data-testid="approve-variance-dialog">
        <h3 className="text-base font-medium mb-3">Approve variance of {Number(batch.variance).toFixed(2)}</h3>
        <label className="block text-xs">Reason<textarea data-testid="av-reason" rows={3} className="w-full mt-1 px-2 py-1.5 border rounded" value={reason} onChange={e => setReason(e.target.value)} /></label>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="av-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="av-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="av-submit" onClick={submit} disabled={busy || !reason.trim()} className="px-3 py-1.5 text-xs text-white bg-emerald-600 rounded">{busy ? "…" : "Approve"}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level shell
// ═══════════════════════════════════════════════════════════════════════════
export default function FinancePage() {
  const [tab, setTab] = useState<"payments" | "cod">("payments");
  return (
    <div className="px-6 py-8 max-w-[1400px] mx-auto" data-testid="finance-page">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Business Reality</div>
        <h1 className="text-3xl md:text-4xl font-serif mt-1" style={{ color: "var(--ph-ink-strong)" }}>Finance <span className="text-slate-400 font-normal text-lg">· Payments + COD</span></h1>
      </header>
      <nav className="inline-flex items-center gap-1 p-1 mb-6" style={{ background: "var(--ph-surface)", border: "1px solid var(--ph-border-subtle)", borderRadius: 8 }} data-testid="finance-tabs">
        {[{ k: "payments", label: "Payments" }, { k: "cod", label: "COD reconciliation" }].map(t => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k as any)}
            className="px-3 py-1.5 text-xs font-medium"
            style={{ background: tab === t.k ? "var(--ph-surface-card)" : "transparent", color: tab === t.k ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)", borderRadius: 6 }}>
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "payments" ? <PaymentsPanel /> : <CODPanel />}
    </div>
  );
}
